import { useState, useEffect } from 'react';
import {
  Package,
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  Server,
  X,
  FileCheck,
  Bug,
  ExternalLink,
} from 'lucide-react';
import type { ComplianceFramework } from '../types/compliance';
import './Products.css';

interface Product {
  id: string;
  name: string;
  status: 'live' | 'beta' | 'alpha' | 'development';
  dataSensitivity: 'low' | 'medium' | 'high' | 'critical';
  frameworks: ComplianceFramework[];
  infrastructure: string[];
  controlsPassing: number;
  controlsTotal: number;
  complianceScore: number;
  lastAudit?: string;
  openIncidents: number;
  description?: string;
  owner?: string;
  repository?: string;
}

export default function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/compliance/products');
        if (res.ok) {
          const data = await res.json();
          setProducts(data);
        } else {
          setError('Failed to load products');
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const getStatusClass = (status: Product['status']) => {
    switch (status) {
      case 'live': return 'status-live';
      case 'beta': return 'status-beta';
      case 'alpha': return 'status-alpha';
      default: return 'status-dev';
    }
  };

  const getSensitivityClass = (sensitivity: Product['dataSensitivity']) => {
    switch (sensitivity) {
      case 'critical': return 'sensitivity-critical';
      case 'high': return 'sensitivity-high';
      case 'medium': return 'sensitivity-medium';
      default: return 'sensitivity-low';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'green';
    if (score >= 70) return 'yellow';
    return 'red';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not audited';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const overallScore = products.length > 0
    ? Math.round(products.reduce((sum, p) => sum + p.complianceScore, 0) / products.length)
    : 0;

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Product Portfolio</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#a6adc8' }}>
          Loading products...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Product Portfolio</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Product Portfolio</h1>
          <p>Compliance status across the PickBits product portfolio</p>
        </div>
      </div>

      {/* Portfolio Overview */}
      <div className="portfolio-overview">
        <div className="portfolio-stat main">
          <Shield size={32} />
          <div className="portfolio-stat-content">
            <span className={`portfolio-score ${getScoreColor(overallScore)}`}>
              {overallScore}%
            </span>
            <span className="portfolio-stat-label">Portfolio Compliance</span>
          </div>
        </div>
        <div className="portfolio-stat">
          <Package size={24} />
          <div className="portfolio-stat-content">
            <span className="portfolio-stat-value">{products.length}</span>
            <span className="portfolio-stat-label">Products</span>
          </div>
        </div>
        <div className="portfolio-stat">
          <CheckCircle size={24} />
          <div className="portfolio-stat-content">
            <span className="portfolio-stat-value">
              {products.filter((p) => p.complianceScore >= 90).length}
            </span>
            <span className="portfolio-stat-label">Fully Compliant</span>
          </div>
        </div>
        <div className="portfolio-stat">
          <AlertTriangle size={24} />
          <div className="portfolio-stat-content">
            <span className="portfolio-stat-value">
              {products.reduce((sum, p) => sum + p.openIncidents, 0)}
            </span>
            <span className="portfolio-stat-label">Open Incidents</span>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="products-grid">
        {products.map((product) => (
          <div key={product.id} className="product-card">
            <div className="product-card-header">
              <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                <div className="product-badges">
                  <span className={`product-status ${getStatusClass(product.status)}`}>
                    {product.status}
                  </span>
                  <span className={`product-sensitivity ${getSensitivityClass(product.dataSensitivity)}`}>
                    {product.dataSensitivity}
                  </span>
                </div>
              </div>
              <div className={`product-score ${getScoreColor(product.complianceScore)}`}>
                {product.complianceScore}%
              </div>
            </div>

            <div className="product-progress">
              <div className="progress-bar">
                <div
                  className={`progress-bar-fill ${getScoreColor(product.complianceScore)}`}
                  style={{ width: `${product.complianceScore}%` }}
                />
              </div>
              <span className="progress-label">
                {product.controlsPassing}/{product.controlsTotal} controls passing
              </span>
            </div>

            <div className="product-frameworks">
              {product.frameworks.map((fw) => (
                <span key={fw} className="framework-badge">{fw}</span>
              ))}
            </div>

            <div className="product-details">
              <div className="product-detail">
                <Server size={14} />
                <span>{product.infrastructure.join(', ')}</span>
              </div>
              <div className="product-detail">
                <Clock size={14} />
                <span>Last audit: {formatDate(product.lastAudit)}</span>
              </div>
              {product.openIncidents > 0 && (
                <div className="product-detail warning">
                  <AlertTriangle size={14} />
                  <span>{product.openIncidents} open incident{product.openIncidents > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            <button
              className="btn btn-secondary product-action"
              onClick={() => setSelectedProduct(product)}
            >
              View Details
            </button>
          </div>
        ))}
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>{selectedProduct.name}</h2>
                <span className={`product-status ${getStatusClass(selectedProduct.status)}`}>
                  {selectedProduct.status}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedProduct(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {selectedProduct.description && (
                <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                  {selectedProduct.description}
                </p>
              )}

              {/* Compliance Score */}
              <div className="product-score-section">
                <div className={`product-score-large ${getScoreColor(selectedProduct.complianceScore)}`}>
                  {selectedProduct.complianceScore}%
                </div>
                <div className="product-score-label">Compliance Score</div>
                <div className="product-progress" style={{ marginTop: '12px' }}>
                  <div className="progress-bar">
                    <div
                      className={`progress-bar-fill ${getScoreColor(selectedProduct.complianceScore)}`}
                      style={{ width: `${selectedProduct.complianceScore}%` }}
                    />
                  </div>
                  <span className="progress-label">
                    {selectedProduct.controlsPassing}/{selectedProduct.controlsTotal} controls passing
                  </span>
                </div>
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Data Sensitivity</span>
                  <span className={`product-sensitivity ${getSensitivityClass(selectedProduct.dataSensitivity)}`}>
                    {selectedProduct.dataSensitivity}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Audit</span>
                  <span>{formatDate(selectedProduct.lastAudit)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Open Incidents</span>
                  <span className={selectedProduct.openIncidents > 0 ? 'text-warning' : ''}>
                    {selectedProduct.openIncidents}
                  </span>
                </div>
                {selectedProduct.owner && (
                  <div className="detail-item">
                    <span className="detail-label">Owner</span>
                    <span>{selectedProduct.owner}</span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <h3>Compliance Frameworks</h3>
                <div className="product-frameworks" style={{ marginTop: '8px' }}>
                  {selectedProduct.frameworks.map((fw) => (
                    <span key={fw} className="framework-badge">{fw}</span>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <h3>Infrastructure</h3>
                <div className="product-infrastructure" style={{ marginTop: '8px' }}>
                  {selectedProduct.infrastructure.map((infra) => (
                    <span key={infra} className="infra-badge">
                      <Server size={12} />
                      {infra}
                    </span>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <a
                  href={`/controls?product=${selectedProduct.id}`}
                  className="btn btn-secondary"
                >
                  <FileCheck size={16} />
                  View Controls
                </a>
                <a
                  href={`/incidents?product=${selectedProduct.id}`}
                  className="btn btn-secondary"
                >
                  <Bug size={16} />
                  View Incidents
                </a>
                {selectedProduct.repository && (
                  <a
                    href={selectedProduct.repository}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                  >
                    <ExternalLink size={16} />
                    Repository
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
